import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import useDriftClient from 'src/hooks/useDriftClient';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { useTxErrorHandler } from 'src/hooks/driftActions/transactions/useTxErrorHandler';
import { notify } from 'src/utils/notifications';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';
import {
	createUpdateMarketMaxLeverageIxs,
	UIMarket,
} from '@velocity-exchange/common';
import { FeePayerError } from 'src/constants/errors/FeePayerError';

interface UseUpdateUserMarketMaxLeverageResult {
	updateUserMarketMaxLeverage: (
		toastId: string,
		marketIndex: number,
		leverage: number
	) => Promise<boolean>;
}

export const useUpdateUserMarketMaxLeverage =
	(): UseUpdateUserMarketMaxLeverageResult => {
		const driftClient = useDriftClient();
		const currentUserAccount = useCurrentUserAccount();

		const getTxParams = useGetTxParams();
		const handleTxError = useTxErrorHandler();

		const updateUserMarketMaxLeverage = useAutoSignDriftActionWithFallback(
			useCallback(
				async (
					signerWallet: UiEnhancedIWalletV2,
					isAutoSigned: boolean,
					toastId: string,
					marketIndex: number,
					leverage: number
				): Promise<boolean> => {
					invariant(driftClient, 'driftClient is required');
					invariant(currentUserAccount?.client, 'user client is required');

					const perpMarketAccount =
						driftClient.getPerpMarketAccount(marketIndex);
					invariant(perpMarketAccount, 'perpMarketAccount is required');

					notify({
						id: toastId,
						type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
						message: isAutoSigned
							? 'Updating position max leverage...'
							: undefined,
						showUntilCancelled: true,
					});

					try {
						const ixs = await createUpdateMarketMaxLeverageIxs({
							velocityClient: driftClient,
							user: currentUserAccount.client,
							perpMarketAccount,
							leverage,
							mainSignerOverride: signerWallet.publicKey,
						});

						if (ixs.length === 0) {
							notify({
								type: 'success',
								message: 'No changes needed.',
								id: toastId,
								updatePrevious: true,
							});
							return true;
						}

						const txParams = getTxParams();
						const txSig = await signerWallet.signAndSendTransaction(
							ixs,
							txParams
						);

						if (!txSig) {
							throw new Error('Transaction failed');
						}

						const marketSymbol =
							UIMarket.createPerpMarket(marketIndex).uiSymbols.marketSymbol;
						notify({
							type: 'success',
							message: `${marketSymbol} leverage updated.`,
							id: toastId,
							updatePrevious: true,
						});

						return true;
					} catch (error) {
						console.error('Error updating market max leverage', error);

						if (error instanceof FeePayerError) {
							throw error;
						}

						handleTxError(error, toastId);

						return false;
					}
				},
				[driftClient, currentUserAccount, getTxParams, handleTxError]
			)
		);

		return { updateUserMarketMaxLeverage };
	};
